import styled from 'styled-components'
import {
  Text,
  Button,
  Checkbox,
  Collapse,
  FontFamiliesInconsolata,
  GreyGrey20,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  IconChevronDown,
  IconChevronTop,
  IconPlus,
  IconSquareBehindSquare,
  IndigoIndigo60,
  Primary12Regular,
  Primary12SemiBold,
  Primary13SemiBold,
  TextInputField,
  Modal,
  useModalState,
  useNotifications,
  Box,
  Flex
} from '@sequencehq/core-components'
import { required } from '@sequencehq/utils/validation'
import { useForm } from '@sequencehq/utils'
import { useState } from 'react'
import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

export type ApiKeyReponse = NonNullable<
  Awaited<ReturnType<typeof dashboard20240730Client.postOAuthClient>>['data']
>

export const CreateAPIKeyModal = () => {
  const { isModalOpen, openModal, closeModal } = useModalState()
  return (
    <>
      <Button
        variant="primary"
        leadingIcon={<IconPlus color={GreyWhite} />}
        onClick={openModal}
        data-testid="createApiKey.trigger"
      >
        New key
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="createApiKeyModal"
      >
        <APIKeyModal onClose={closeModal} />
      </Modal>
    </>
  )
}

const APIKeyModal = ({ onClose }: { onClose: () => void }) => {
  const { displayNotification } = useNotifications()
  const [apiKey, setAPIKey] = useState<ApiKeyReponse | null>(null)
  const accountIdQuery = useQuery(dashboard20240730Client.getSequenceAccountId)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [showCurlExample, setShowCurlExample] = useState(false)
  const sequenceAccountId = accountIdQuery.data?.id

  const { mutate, isPending } = useMutation(
    dashboard20240730Client.postOAuthClient,
    {
      onSuccess(data) {
        setAPIKey(data)
        displayNotification('API key successfully created', {
          type: 'success'
        })
      },
      onError() {
        displayNotification('API key failed to create', {
          type: 'error'
        })
      }
    }
  )

  const { fields, queries } = useForm({
    value: {
      name: '',
      pinApiVersion: false
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'pinApiVersion',
        validation: []
      }
    ]
  })

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        displayNotification('Copied to clipboard', {
          type: 'success',
          duration: 3000
        })
      })
      .catch(() => {
        displayNotification('Failed to copy to clipboard', {
          type: 'error'
        })
      })
  }

  if (apiKey) {
    const base64Token = window.btoa(`${apiKey.id}:${apiKey.secret}`)
    const authHeader = `Authorization: Basic ${base64Token}`
    const apiOrigin =
      import.meta.env.VITE_API_ORIGIN ?? 'https://eu.sequencehq.com'
    const curlExample = [
      'curl --request GET \\',
      `  --url ${apiOrigin}/api/customers \\`,
      `  --header 'Authorization: Basic ${base64Token}'`
    ].join('\n')

    return (
      <>
        <Modal.Header
          showCloseButton
          onClose={onClose}
          withBorder
          {...Primary13SemiBold}
        >
          API key &ldquo;{apiKey.name}&rdquo; created
        </Modal.Header>
        <Modal.Content minHeight="auto">
          <Flex flexDirection="column" gap="8px">
            <Flex flexDirection="column" gap="12px">
              <CredentialField
                label="API token"
                id="api-token"
                value={base64Token}
                onCopy={() => copyToClipboard(base64Token)}
              />

              <CredentialField
                label="Client ID"
                id="client-id"
                value={apiKey.id}
                onCopy={() => copyToClipboard(apiKey.id)}
              />

              <CredentialField
                label="Client secret"
                id="client-secret"
                value={apiKey.secret}
                type="password"
                onCopy={() => copyToClipboard(apiKey.secret)}
              />
            </Flex>

            <Text {...Primary12Regular} color={GreyGrey60}>
              These credentials won't be shown again. Copy them now.
            </Text>

            <Flex flexDirection="column" gap="4px" paddingTop="8px">
              <Box
                data-testid="createApiKey.showUsageExamples"
                display="flex"
                gap="4px"
                alignItems="center"
                cursor="pointer"
                userSelect="none"
                onClick={() => setShowCurlExample(prev => !prev)}
              >
                <Text {...Primary13SemiBold} color={IndigoIndigo60}>
                  {showCurlExample ? 'Hide' : 'Show'} usage examples
                </Text>
                {showCurlExample ? (
                  <IconChevronTop color={IndigoIndigo60} />
                ) : (
                  <IconChevronDown color={IndigoIndigo60} />
                )}
              </Box>
              <Collapse in={showCurlExample} animateOpacity>
                <Flex flexDirection="column" gap="8px" paddingTop="4px">
                  <CodeBlock
                    title="HTTP header"
                    code={authHeader}
                    onCopy={() => copyToClipboard(authHeader)}
                  />
                  <CodeBlock
                    data-testid="createApiKey.usageExample.curl"
                    title="cURL"
                    code={curlExample}
                    onCopy={() => copyToClipboard(curlExample)}
                    multiline
                  />
                </Flex>
              </Collapse>
            </Flex>
          </Flex>
        </Modal.Content>

        <Modal.Footer withBorder>
          <Button
            variant="primary"
            onClick={onClose}
            style={{ marginLeft: 'auto' }}
          >
            Done
          </Button>
        </Modal.Footer>
      </>
    )
  }

  return (
    <>
      <Modal.Header
        showCloseButton
        onClose={onClose}
        withBorder
        {...Primary13SemiBold}
      >
        Create API key
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex
          id="create-api-key"
          as="form"
          flexDirection="column"
          gap="16px"
          onSubmit={e => {
            if (!sequenceAccountId) return
            e.preventDefault()
            setShowValidationErrors(true)
            if (!queries.isValid) return
            mutate({
              accountId: sequenceAccountId,
              clientName: queries.formData.name,
              pinApiVersion: queries.formData.pinApiVersion,
              scopes: ['product']
            })
          }}
        >
          <TextInputField
            label="Name"
            data-testid="createApiKey.name"
            autoFocus
            id="clientName"
            placeholder="Name"
            disabled={isPending}
            value={fields.name.value}
            validationErrors={fields.name.validationErrors}
            onChange={e => {
              fields.name.onChange(e.target.value)
            }}
          />

          <Checkbox
            data-testid="createApiKey.pinVersion"
            isChecked={fields.pinApiVersion.value}
            label="Pin API key to latest API version"
            id="pin-api-key-to-latest"
            onChange={e => {
              fields.pinApiVersion.onChange(e.target.checked)
            }}
          />
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Flex justifyContent="end" width="100%" alignItems="center" gap="8px">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Modal.SubmitButton
            data-testid="createApiKey"
            form="create-api-key"
            disabled={isPending || !sequenceAccountId}
            submitting={isPending}
            text="Create"
            submittingText="Creating..."
          />
        </Flex>
      </Modal.Footer>
    </>
  )
}

const CodeBlock = ({
  title,
  code,
  onCopy,
  multiline = false,
  'data-testid': dataTestId
}: {
  title: string
  code: string
  onCopy: () => void
  multiline?: boolean
  'data-testid'?: string
}) => (
  <Box
    data-testid={dataTestId}
    border={`1px solid ${GreyGrey40}`}
    borderRadius="6px"
    overflow="hidden"
    backgroundColor={GreyWhite}
  >
    <Flex
      height="32px"
      backgroundColor={GreyGrey20}
      paddingX="12px"
      alignItems="center"
      justifyContent="space-between"
      borderBottom={`1px solid ${GreyGrey40}`}
    >
      <Text {...Primary12SemiBold} color={GreyGrey70}>
        {title}
      </Text>
      <Button
        variant="ghost"
        style={{ width: '24px', height: '24px', borderRadius: '4px' }}
        onClick={onCopy}
        icon={<IconSquareBehindSquare color={GreyGrey70} />}
        aria-label="Copy to clipboard"
      />
    </Flex>
    <Box padding="10px 12px" overflowX="auto">
      <CodeText $multiline={multiline}>{code}</CodeText>
    </Box>
  </Box>
)

const CodeText = styled.code<{ $multiline: boolean }>`
  display: block;
  font-family: ${FontFamiliesInconsolata};
  font-size: 12px;
  color: ${GreyGrey80};
  padding: 0 0.2em;
  white-space: ${({ $multiline }) => ($multiline ? 'pre' : 'nowrap')};
`

const CredentialField = ({
  label,
  id,
  value,
  onCopy,
  type
}: {
  label: string
  id: string
  value: string
  onCopy: () => void
  type?: 'text' | 'password'
}) => (
  <Flex flexDirection="column" gap="8px">
    <Text {...Primary13SemiBold} color={GreyGrey70} as="label" htmlFor={id}>
      {label}
    </Text>
    <Flex gap="8px" alignItems="flex-start">
      <TextInputField
        id={id}
        value={value}
        readOnly
        type={type}
        onChange={() => undefined}
        styles={{ wrapper: { flex: 1 } }}
      />
      <Button variant="secondary" onClick={onCopy}>
        Copy
      </Button>
    </Flex>
  </Flex>
)
